import React from "react";
import { groupIndustriesByTier } from "../../utils/industryUtils";
import { Link } from "react-router-dom";

const IndustryTier = ({ title, industries }) => {
  if (!industries.length) return null;

  return (
    <div className="mb-1 flex items-center shadow shadow-sm border bg-[var(--entity-mini)] p-1 rounded-md">
      <h4 className="text-sm panel-title">{title === "Untiered" ? "" : `${title}:`}</h4>
      <div className="flex flex-wrap gap-1">
        {industries.map(({ key, name, count }, i) => {
          const img = key.startsWith("tree")
            ? `https://pixelore.s3.eu-central-003.backblazeb2.com/Industries/ent_tree.png`
            : `https://pixelore.s3.eu-central-003.backblazeb2.com/Industries/${key}.png`;
          return (
            <div key={i} className="flex items-center gap-1 rounded p-1">
              <img src={img} alt={name} className="w-7 h-7" title={name} />
              <div>
                <p className="text-sm text-[var(--text-color)]">x{count}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LandCard = ({ land, minimal = false }) => {
  const imgUrl = `https://hang.mypinata.cloud/ipfs/QmRJSjDZqQu5yoUbBtMXhLp4T9czmpUmfpBg6ntLzbjMUV/${land.landNum}.png`;

  // Common elements for both views
  const renderBasicInfo = () => (
    <>
      <h2 className={minimal ? "hidden" : "text-xl font-bold text-center mb-2"}>{land.landName || `Land #${land.landNum}`}</h2>
      <div className="flex flex-col lg:flex-row">
        <div className={minimal ? "flex flex-col items-center mb-4 lg:mb-0 mx-auto" : "flex flex-col items-center mb-4 lg:mb-0 lg:mr-4"}>
          <div className="text-xs font-bold">#{land.landNum}</div>
          <img src={imgUrl} alt={land.landNum} className="w-32 h-26 mx-auto" />
          <div className={minimal ? `flex flex-wrap gap-1 mt-2` : "flex flex-wrap justify-center gap-1 mt-2 lg:p-1 lg:gap-0 lg:my-auto lg:block"}>
            <div className="panel-button text-xs text-center">
              <i className="fa-solid fa-mountain-sun" /> {land.landSize}
            </div>
            <div className="panel-button text-xs text-center">
              <i className="fa-solid fa-seedling" /> {land.environment}
            </div>
            <div className="panel-button text-xs text-center">
              <i className="fa-solid fa-house" /> {land.houseSize}
            </div>
            <div className="panel-button text-xs text-center">
              <i className="fa-solid fa-tree" /> {land.treeDensity ?? "None"}
            </div>
            <div className="panel-button text-xs text-center">
              {land.permissions?.includes("Public") ? <i className="fa-solid fa-lock-open" /> : <i className="fa-solid fa-lock" />}{" "}
              {land.permissions?.[0] || "Private"}
            </div>
          </div>
        </div>
        {!minimal && renderIndustries()}
      </div>
      <div className="mt-2">
        {land.guild && (
          <div className="text-xs">
            <span className="item-title">Guild:</span> {land.guild}
          </div>
        )}
        {land.owner && (
          <div className="text-xs">
            <span className="item-title">Land Owner:</span> <Link to={`../lookup/players/${land.owner}`} target="_blank" rel="noreferrer">{land.owner}</Link>
          </div>
        )}
      </div>
    </>
  );

  const renderIndustries = () => {
    const industryData = (land.entities || []).reduce((combined, entity) => {
      return {
        ...combined,
        ...(entity.entItems || {}),
        ...(entity.bstItems || {}),
        ...(entity.otherEntities || {}),
      };
    }, {});

    const houseIndustryData = (land.houseEntities || []).reduce((combined, entity) => {
      return {
        ...combined,
        ...(entity.entItems || {}),
        ...(entity.bstItems || {}),
        ...(entity.otherEntities || {}),
      };
    }, {});
    const industryGroups = groupIndustriesByTier(industryData);
    const houseIndustryGroups = groupIndustriesByTier(houseIndustryData);

    return (
      <div className="flex flex-col lg:flex-row w-full gap-4">
        <div className="flex-1">
          <h3 className="font-semibold text-lg">Land Industries</h3>
          {Object.entries(industryGroups).map(([tier, list]) => (
            <IndustryTier key={tier} title={tier} industries={list} />
          ))}
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-lg">House Industries</h3>
          {Object.entries(houseIndustryGroups).map(([tier, list]) => (
            <IndustryTier key={tier} title={tier} industries={list} />
          ))}
        </div>
      </div>
    );
  };

  return <div className={`border rounded-xl shadow component-style p-4 ${minimal ? "minimal-card" : ""}`}>{renderBasicInfo()}</div>;
};

export default LandCard;
